import scriptjs from 'scriptjs';
import constants from '../../../qti/Constants';
import stringUtils from '../../../qti/utils/StringUtils';
import sessionReader from '../../../store/readHelpers/SessionReader';
import a11yIIBScreenReaderService, {
    A11yForIIBScreenReaderIteration,
} from '../../../services/a11yIIBScreenReaderService';

let mathJaxInitialized = false;
let mathJaxInitInProgress = false;

function configureMathJax() {
    window.MathJax = {
        tex: {
            inlineMath: [['\\(', '\\)']],
            displayMath: [],
        },
        options: {
            enableMenu: false,
        },
    };
}

/**
 * Maps a fence character (used by <mfenced>'s open/close attributes) to a spoken label.
 * @param {string} char - The fence character, or '' for no fence.
 * @returns {string} A screen-reader-friendly description, or '' if there's nothing to say.
 */
function getFenceLabel(char) {
    switch (char) {
        case '(': return 'open parenthesis';
        case ')': return 'close parenthesis';
        case '[': return 'open bracket';
        case ']': return 'close bracket';
        case '{': return 'open brace';
        case '}': return 'close brace';
        case '|': return 'vertical bar';
        case '': return '';
        default: return char;
    }
}

export function extractMathLabel(mathElement) {
    if (!mathElement) return null;

    function processNode(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent.trim();
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const tag = node.tagName.toLowerCase().replace(/^[^:]+:/, '');
        const children = Array.from(node.childNodes).map(processNode).filter(t => t !== '');

        switch (tag) {
            case 'math':
            case 'mrow':
            case 'mstyle':
            case 'mpadded':
                return children.join(' ');
            case 'mi':
            case 'mn':
            case 'mtext':
                return children.join('');
            case 'mo': {
                const opText = children.join('');
                if (opText === '!') return 'factorial';
                return getFenceLabel(opText);
            }
            case 'msup': {
                if (children.length >= 2) {
                    if (children[1] === '2') return `${children[0]} squared`;
                    if (children[1] === '3') return `${children[0]} cubed`;
                    return `${children[0]} to the power of ${children[1]}`;
                }
                return children.join(' ');
            }
            case 'msub':
                return children.length >= 2 ? `${children[0]} sub ${children[1]}` : children.join(' ');
            case 'msubsup':
                return children.length >= 3
                    ? `${children[0]} sub ${children[1]} to the power of ${children[2]}`
                    : children.join(' ');
            case 'mfrac':
                return children.length >= 2 ? `${children[0]} over ${children[1]}` : children.join(' ');
            case 'msqrt':
                return children.length ? `square root of ${children.join(' ')}` : 'square root';
            case 'mroot':
                return children.length >= 2 ? `${children[1]} root of ${children[0]}` : children.join(' ');
            case 'munderover':
                // Bounded large operators (e.g. a summation with limits), mirroring the
                // "X from Y to Z" framing used for the equivalent MathQuill construct.
                return children.length >= 3
                    ? `${children[0]} from ${children[1]} to ${children[2]}`
                    : children.join(' ');
            case 'munder':
                return children.length >= 2 ? `${children[0]} under ${children[1]}` : children.join(' ');
            case 'mover':
                return children.length >= 2 ? `${children[0]} over ${children[1]}` : children.join(' ');
            case 'mfenced': {
                const openLabel = getFenceLabel(node.hasAttribute('open') ? node.getAttribute('open') : '(');
                const closeLabel = getFenceLabel(node.hasAttribute('close') ? node.getAttribute('close') : ')');
                const separator = (node.hasAttribute('separators') ? node.getAttribute('separators').charAt(0) : ',') || ',';
                const content = children.join(`${separator} `);
                return [openLabel, content, closeLabel].filter(t => t !== '').join(' ');
            }
            case 'mtable':
                return children.length ? `matrix ${children.join(', ')}` : '';
            case 'mtr':
                return children.length ? `row ${children.join(' ')}` : '';
            case 'annotation':
            case 'annotation-xml':
                // These carry the raw source (e.g. TeX) alongside the presentation tree in <semantics>;
                // they duplicate the equation and must not be spoken as part of the label.
                return '';
            default:
                return children.join(' ');
        }
    }

    return processNode(mathElement)?.trim() || null;
}

export function patchMathJaxAccessibility(wrapperElement) {
    if (!a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v7)) {
        return;
    }

    const scope = wrapperElement || document;
    scope.querySelectorAll('mjx-container').forEach((container) => {
        const assistiveMml = container.querySelector('mjx-assistive-mml');
        const mmlMath = assistiveMml?.querySelector('math');

        const label = container.getAttribute('aria-label') || extractMathLabel(mmlMath);
        if (!label) {
            // Keep MathJax's built-in assistive content when we can't derive a reliable label,
            // otherwise the equation would be silent for screen readers.
            return;
        }

        container.setAttribute('role', 'img');
        container.setAttribute('aria-label', label);

        if (assistiveMml) {
            assistiveMml.setAttribute('aria-hidden', 'true');
        }

        const mjxMath = container.querySelector('mjx-math');
        if (mjxMath && !mjxMath.hasAttribute('aria-hidden')) {
            mjxMath.setAttribute('aria-hidden', 'true');
        }
    });
}

async function getMathJaxInstanceAndDoInitTypeset() {
    if (window.MathJax) {
        return window.MathJax;
    }
    mathJaxInitInProgress = true;

    return new Promise((resolve, reject) => {
        let mathJaxUrl;
        if (sessionReader?.isOfflineMode()) {
            mathJaxUrl = `${constants.staticLibPathsOffline.MATHJAX3}tex-chtml.js`;
        } else if (sessionReader?.isViewMediaPrintOrPdf()) {
            mathJaxUrl = `${constants.staticLibPaths.MATHJAX3}tex-svg.js`;
        } else {
            mathJaxUrl = `${constants.staticLibPaths.MATHJAX3}tex-chtml.js`;
        }

        configureMathJax();
        window.MathJax.startup = {
            ready: async () => {
                try {
                    await window.MathJax.startup.defaultReady();
                    patchMathJaxAccessibility(document);
                    console.log('MathJax initial typesetting complete');
                    resolve(window.MathJax);
                    mathJaxInitInProgress = false;
                } catch (e) {
                    reject(e);
                }
            },
        };
        scriptjs(mathJaxUrl, () => {
            if (!window.MathJax) {
                reject('MathJax 3 could not be loaded!');
            }
        });
    });
}

export function removeStartEndSequence(latexString) {
    if (typeof latexString !== 'string') {
        return latexString;
    }
    return latexString.replace(/\\\(|\\\)/g, '');
}

/**
 * When preloading, render the math elements manually and append them to the preload container.
 * If doing regular render (as when displaying then and replacing), the math elements on the page you are viewing
 * are also affected by blinking shortly as the page is scanned.
 * @param wrapperElement
 */
function preloadMathOnPage(wrapperElement) {
    Array.from(wrapperElement.getElementsByClassName('math-tex')).forEach((element) => {
        const textToPreload = removeStartEndSequence(stringUtils.unescapeXML(element.innerHTML));
        try {
            const html = window.MathJax.tex2chtml(textToPreload, { display: false });
            wrapperElement.appendChild(html);
        } catch (e) {
            console.warn('Error when preloading Mathjax', e);
        }
    });
    // Adding css needed to display the current math elements. This triggers loading of fonts needed for the new expressions
    document.head.appendChild(window.MathJax.chtmlStylesheet());
}

const MathJax3Display = {
    async showMathJax3(wrapperElement) {
        if (mathJaxInitInProgress || !wrapperElement || wrapperElement.getElementsByClassName('math-tex').length === 0) {
            return;
        }
        const isPreload = wrapperElement.getAttribute('id')?.includes('preload');
        if (mathJaxInitialized && isPreload) {
            preloadMathOnPage(wrapperElement);
            return;
        }
        if (!mathJaxInitialized) {
            try {
                await getMathJaxInstanceAndDoInitTypeset();
                mathJaxInitialized = true;
                return;
            } catch (e) {
                console.error('Unable to load Mathjax 3', e);
                return;
            }
        }
        await window.MathJax.typesetPromise();
        patchMathJaxAccessibility(wrapperElement);
    },
};

export default MathJax3Display;
